import { computed, onMounted, ref } from "vue";

export type InputEmits<TValue> = {
  (e: "input", value: TValue | undefined): void;
  (e: "blur"): void;
};

export interface InputFieldGroupProps {
  name?: string;
  label?: string;
  errors?: string[];
  note?: string;
  noteClasses?: string;
  required?: boolean;
}

export interface InputFieldProps<TValue> extends InputFieldGroupProps {
  placeholder?: string;
  value?: TValue;
  disabled?: boolean;
  loading?: boolean;
}

interface InputField extends Element {
  focus: () => void;
  blur: () => void;
}

function isInputField(object: any): object is InputField {
  if (Array.isArray(object) || !object) return false;

  return "focus" in object; //&& "otherProp" in object;
}

function isInputFieldArray(object: any): object is InputField[] {
  if (Array.isArray(object)) {
    return object.every(x => "focus" in x);
  }
  return false;
}

export function useInputField<TInput, TValue>({ props, emit, emitTransform }: { props: InputFieldProps<TValue>; emit: InputEmits<TValue>; 
  emitTransform?: (val?: TValue) => (TValue)}) {
  const input = ref<TInput>();

  const innerValue = computed({
    get() {
      return props.value;
    },
    set(val?: TValue) {
      if(emitTransform){
        val = emitTransform(val)
      } 
      emit("input", val);
    },
  });

  const hasValue = computed(() => props.value !== undefined && props.value !== null);
  const hasErrors = computed(() => props.errors && props.errors.length > 0);

  const focus = () => {
    if (isInputField(input.value)) {
      input.value?.focus();
      return;
    }
    throw "Invalid Input Type";
  };

  const blur = () => {
    if (isInputField(input.value)) {
      input.value?.blur();
      return;
    }
    throw "Invalid Input Type";
  };

  onMounted(() => {
    if (isInputField(input.value)) {
      (input.value as unknown as Element).addEventListener("blur", () => emit("blur"));
    }
  });

  return {
    input,
    innerValue,
    hasValue,
    hasErrors,
    focus,
    blur,
  };
}

export function useInputArrayField<TInput, TValue>({ props, emit }: { props: InputFieldProps<TValue>; emit: InputEmits<TValue> }) {
  const input = ref<TInput[]>();

  const innerValue = computed({
    get() {
      return props.value;
    },
    set(val?: TValue) {
      emit("input", val);
    },
  });

  const hasValue = computed(() => props.value !== undefined && props.value !== null);
  const hasErrors = computed(() => props.errors && props.errors.length > 0);

  const focus = () => {
    if (isInputFieldArray(input.value)) {
      input.value?.forEach((x: InputField) => x.focus());
    }
    return;
  };

  const blur = () => {
    if (isInputFieldArray(input.value)) {
      input.value?.forEach((x: InputField) => x.blur());
      return;
    }
    throw "Invalid Input Type";
  };

  onMounted(() => {
    if (isInputFieldArray(input.value)) {
      input.value.forEach((x: InputField) => x.addEventListener("blur", () => emit("blur")));
    }
  });

  return {
    input,
    innerValue,
    hasValue,
    hasErrors,
    focus,
    blur,
  };
}
